import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';

interface ShippingLabelProps {
  shipTo: {
    name: string;
    address: string;
    city: string;
    postalCode: string;
  };
  customerNumber: string;
  orderNumber: string;
  paymentType: string;
  collectedAmount: number;
  items: Array<{
    sku: string;
    product: string;
    qty: number;
    amount: number;
  }>;
  pickupAddress: {
    name: string;
    address: string;
    city: string;
    postalCode: string;
    contact: string;
    email: string;
  };
  courierName?: string;
  trackingNumber: string;

  // Branding
  showLogo: boolean;
  logoText: string;
  instagramQR: boolean;
  instagramUrl: string;
  showParcelpicksBranding: boolean;
  showYourBranding: boolean;

  // Visibility flags (aligned with LabelData)
  customerName: boolean;
  customerNumberVisible: boolean; // Renamed to avoid collision with data
  customerEmail: boolean;
  paymentMode: boolean;
  customerAddress: boolean;
  routingCode: boolean;
  showDimensions: boolean;
  showWeight: boolean;

  product: boolean;
  sku: boolean;
  qty: boolean;
  amount: boolean;

  pickupNameVisible: boolean;
  pickupAddressVisible: boolean;
  pickupContactVisible: boolean;
  pickupEmailVisible: boolean;

  note: boolean;
  noteText: string;

  // System/Other
  showOrderNumber: boolean;
  showCourierSection: boolean;
  showPaymentSection: boolean;
  showQRCode: boolean;
  showSystemFooter: boolean;

  logoUrl: string;
  weight?: number;
  weightUnit?: string;
  dimensions?: { length: number; breadth: number; height: number };
  isThermal?: boolean;
}

export function ShippingLabel({
  shipTo,
  customerNumber,
  orderNumber,
  paymentType,
  collectedAmount,
  items,
  pickupAddress,
  courierName,
  trackingNumber,

  showLogo,
  logoText,
  instagramQR,
  instagramUrl,
  showParcelpicksBranding,
  showYourBranding,

  customerName,
  customerNumberVisible,
  customerEmail,
  paymentMode,
  customerAddress,
  routingCode,
  showDimensions,
  showWeight,

  product,
  sku,
  qty,
  amount,

  pickupNameVisible,
  pickupAddressVisible,
  pickupContactVisible,
  pickupEmailVisible,

  note,
  noteText,

  showOrderNumber,
  showCourierSection,
  showPaymentSection,
  showQRCode,
  showSystemFooter,

  logoUrl,
  weight = 0.5,
  weightUnit = 'Kg',
  dimensions = { length: 10, breadth: 10, height: 10 },
  isThermal = false,
}: ShippingLabelProps) {
  // Logic for branding borders
  const rawBrand = showYourBranding
    ? logoText || 'Your Brand'
    : showParcelpicksBranding
      ? 'Parcelpicks'
      : '';
  const displayBrand = rawBrand.length > 10 ? rawBrand.substring(0, 10) : rawBrand;

  const showBrandingBorders = !isThermal && (showParcelpicksBranding || showYourBranding);

  const displayLogo = showLogo ? logoUrl : '';

  const horizontalRepeat = 5;
  const verticalRepeat = 8;

  // The branded strip width/height — keep in sync with the w-5/h-5 classes below
  const STRIP = 20; // px, matches w-5 / h-5 (20px)

  const rawCourier = courierName || 'XpressBees Surface';
  const cleanCourier = rawCourier
    .replace(/by surface/gi, 'Surface')
    .replace(/by air/gi, 'Air')
    .replace(/DELHIVERY\s*\(PE\)/gi, 'DELHIVERY')
    .replace(/\(PE\)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  const hasMode = /surface|air/i.test(cleanCourier);
  const formattedCourierName = (hasMode ? cleanCourier : `${cleanCourier} - Surface`).toUpperCase();

  return (
    <div className="w-full flex items-center justify-center">
      {/*
        Outer container: holds the branded strips + outer border.
        border-2 draws the outermost visible frame.
      */}
      <div className={`mx-auto bg-white border-2 border-black text-black font-sans w-full h-full p-0 flex flex-col relative`}>
        {showBrandingBorders && (
          <>
            {/* ── TOP BRANDED STRIP ── */}
            <div
              className="absolute top-0 left-0 right-0 flex items-center justify-around bg-white  border-black z-10"
              style={{ height: STRIP }}
            >
              {[...Array(horizontalRepeat)].map((_, i) => (
                <span
                  key={i}
                  className="text-[10px] font-bold text-teal leading-none"
                >
                  {displayBrand}
                </span>
              ))}
            </div>

            {/* ── BOTTOM BRANDED STRIP ── */}
            <div
              className="absolute bottom-0 left-0 right-0 flex items-center justify-around bg-white  border-black z-10"
              style={{ height: STRIP }}
            >
              {[...Array(horizontalRepeat)].map((_, i) => (
                <span
                  key={i}
                  className="text-[10px] font-bold text-teal leading-none"
                >
                  {displayBrand}
                </span>
              ))}
            </div>

            {/* ── LEFT BRANDED STRIP ── */}
            <div
              className="absolute left-0 flex flex-col items-center justify-around bg-white  border-black z-10 overflow-hidden"
              style={{ top: STRIP, bottom: STRIP, width: STRIP }}
            >
              {[...Array(verticalRepeat)].map((_, i) => (
                <span
                  key={i}
                  className="text-[10px] font-bold text-teal -rotate-90 whitespace-nowrap"
                >
                  {displayBrand}
                </span>
              ))}
            </div>

            {/* ── RIGHT BRANDED STRIP ── */}
            <div
              className="absolute right-0 flex flex-col items-center justify-around bg-white  border-black z-10 overflow-hidden"
              style={{ top: STRIP, bottom: STRIP, width: STRIP }}
            >
              {[...Array(verticalRepeat)].map((_, i) => (
                <span
                  key={i}
                  className="text-[10px] font-bold text-teal rotate-90 whitespace-nowrap"
                >
                  {displayBrand}
                </span>
              ))}
            </div>
          </>
        )}

        {/*
          INNER CONTENT AREA
          Padded by STRIP px on all sides so it sits exactly inside the branded strips.
          The border-2 here creates the inner frame that "encloses" the strips.
          Corners won't look joined to side strips because the inner border starts
          right at the edge of each strip — giving a clean T-junction instead of
          a corner-gap.
        */}
        <div
          className={!isThermal ? "border-2 border-black" : ""}
          style={{
            margin: showBrandingBorders ? STRIP : 0,
          }}
        >
          {/* ── SHIP TO ── */}
          <div className="border-b border-black px-0 mb-4 relative">
            <div className="flex p-2 justify-between">
              <div className="flex-1">
                <div className="text-sm mb-2">
                  <strong>SHIP TO :</strong>
                </div>
                <div className="text-sm leading-relaxed">
                  <div className={customerName ? '' : 'invisible'}>
                    {shipTo.name || 'John Doe'}
                  </div>
                  <div className={customerAddress ? '' : 'invisible'}>
                    <div>{shipTo.address || 'Placeholder Address'}</div>
                    <div>{shipTo.city || 'Placeholder City'}</div>
                    <div>{shipTo.postalCode || '000000'}</div>
                  </div>
                  <div className={customerNumberVisible ? '' : 'invisible'}>
                    Customer Number: {customerNumber || '9999999999'}
                  </div>
                  <div className={customerEmail ? '' : 'invisible'}>
                    Customer Email: test@customer.com
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end mr-8">
                <div className="w-20 h-20 flex items-center justify-center">
                  {displayLogo && (
                    <img
                      src={displayLogo}
                      alt="logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3 p-2 border-t flex justify-between border-black">
              <div className="text-sm mb-1 space-y-0.5">
                <div>Order Id: #{orderNumber}</div>
                <div className={paymentMode && showPaymentSection ? '' : 'invisible'}>
                  Order Type: {paymentType}{' '}
                  {paymentType === 'COD' ? `(₹${collectedAmount})` : ''}
                </div>
                <div className={showDimensions ? '' : 'invisible'}>
                  Dimensions : {dimensions.length} X {dimensions.breadth} X{' '}
                  {dimensions.height}
                </div>
                <div className={showWeight ? '' : 'invisible'}>
                  Weight : {weight} {weightUnit}
                </div>
              </div>
              <div className={'flex justify-end -mb-2'}>
                <Barcode
                  value={orderNumber || '000000'}
                  height={40}
                  width={1.5}
                  fontSize={12}
                  margin={0}
                />
              </div>
            </div>
          </div>

          {/* ── COURIER SECTION ── */}
          <div className="border-b border-black p-2 mb-4">
            <div className="flex p-2 items-center justify-between">
              <div className={routingCode ? '' : 'invisible'}>
                <div className="text-2xl font-bold uppercase">{formattedCourierName}</div>
                <div className="text-2xl font-bold">{shipTo.postalCode || '000000'}</div>
              </div>
              <div className="-mb-2">
                <Barcode
                  value={trackingNumber || '000000'}
                  height={50}
                  width={1.5}
                  fontSize={12}
                  margin={0}
                />
              </div>
            </div>
          </div>

          {/* ── ITEMS TABLE ── */}
          <div className="border-b border-black mb-4 p-8">
            <table className="border border-black w-full">
              <thead>
                <tr className="border-b border-black">
                  {product && (
                    <th className="border-r border-black p-2 text-sm text-left bg-gray-100">
                      Product
                    </th>
                  )}
                  {sku && (
                    <th className="border-r border-black p-2 text-sm text-center bg-gray-100 w-24">
                      SKU
                    </th>
                  )}
                  {qty && (
                    <th className="border-r border-black p-2 text-sm text-center bg-gray-100 w-20">
                      QTY
                    </th>
                  )}
                  {amount && (
                    <th className="p-2 text-sm text-center bg-gray-100 w-24">
                      Amount
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item, i) => (
                    <tr
                      key={i}
                      className="border-b border-black last:border-b-0 h-10"
                    >
                      {product && (
                        <td className="border-r border-black p-2 text-sm text-left">
                          {item.product}
                        </td>
                      )}
                      {sku && (
                        <td className="border-r border-black p-2 text-sm text-center">
                          {item.sku}
                        </td>
                      )}
                      {qty && (
                        <td className="border-r border-black p-2 text-sm text-center">
                          {item.qty}
                        </td>
                      )}
                      {amount && (
                        <td className="p-2 text-sm text-center">
                          {item.amount}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={
                        [product, sku, qty, amount].filter(Boolean).length
                      }
                      className="h-10 text-center text-sm text-gray-400"
                    >
                      Items hidden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── RETURN ADDRESS ── */}
          <div className=" border-black p-3 mb-4">
            <div className="flex justify-between items-start">
              <div className="flex-1 px-0">
                <div className="text-sm mb-2">
                  <strong>RETURN ADDRESS (FROM) :</strong>
                </div>
                <div className="text-sm leading-relaxed">
                  <div className={pickupNameVisible ? '' : 'invisible'}>
                    {pickupAddress.name || 'Return Name'}
                  </div>
                  <div className={pickupAddressVisible ? '' : 'invisible'}>
                    <div>{pickupAddress.address || 'Placeholder Address'}</div>
                    <div>{pickupAddress.city || 'Placeholder City'}</div>
                    <div>{pickupAddress.postalCode || '000000'}</div>
                  </div>
                  <div className={pickupContactVisible ? '' : 'invisible'}>
                    Contact: {pickupAddress.contact || '9999999999'}
                  </div>
                  <div className={pickupEmailVisible ? '' : 'invisible'}>
                    Email: {pickupAddress.email || 'return@placeholder.com'}
                  </div>
                </div>
              </div>

              {instagramQR && (
                <div className="flex flex-col items-center pr-6">
                  <QRCodeSVG
                    value={instagramUrl || 'https://parcelpicks.com/track'}
                    size={80}
                  />
                  <div className="text-sm mt-1">Scan Me</div>
                </div>
              )}
            </div>
          </div>

          {/* ── NOTE ── */}
          <div className={`border-t border-black py-4 px-8 mb-4 ${note ? '' : 'invisible'}`}>
            <div className="text-sm">
              <strong className="text-base">Note</strong>
              <p className="p-2 border border-black mt-1">
                {noteText ||
                  'Please do not accept the shipment if the package appears tampered.'}
              </p>
            </div>
          </div>
          <div className="flex flex-row items-center justify-between border-2 text-xs px-2 border-t-black ">
            <p>This is a system generated document</p>
            <p>Powered by Parcelpicks</p>
          </div>
        </div>
        {/* end inner content */}
      </div>
    </div>
  );
}
